/**
 * Fase A — Marcador flotante (FAB) estilo call center: teclado + historial reciente.
 * Solo visible con feature `crm`. Reutiliza `call-center-start-call` y `useCallCenter`.
 * UI: en reposo teclado + historial; en timbrado/conversación misma “pantalla de llamada” que un móvil:
 *    avatar + ondas + Timbrando / En llamada, Colgar, y CRM a la derecha (un solo teléfono, distintas fases).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BlockStack,
  Box,
  Button,
  Text,
  TextField,
  InlineStack,
  Banner,
  Spinner,
  Card,
  Scrollable,
  InlineGrid,
  Divider,
  Icon,
} from '@/lib/polaris';
import { BackspaceIcon, ClipboardIcon, DuplicateIcon, PhoneIcon, XIcon } from '@/lib/polaris-icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/config/routes.config';
import {
  FAB_DIALER_LEFT_FLEX,
  FAB_DIALER_LEFT_MAX_WIDTH,
  FAB_DIALER_RIGHT_FLEX,
} from '@/config/callCenterFabDialerLayout';
import { crmCallStatusLabelEs } from '@/lib/crmCallStatusLabels';
import { syncWebRtcStoreFromOutboundCrmInsert } from '@/lib/callCenterDialContext';
import { playDtmfKey, primeDtmfAudio, resumeAllContextsForSyntheticRingback } from '@/lib/dtmfKeypad';
import { mapKeyboardEventToDialDigit } from '@/lib/callCenter/dialerKeyboard';
import { useDialerPhysicalKeyboard } from '@/hooks/useDialerPhysicalKeyboard';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useCallCenter, type CallCenterContextValue } from '@/contexts/CallCenterContext';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { CallCenterPhoneActiveSession } from '@/components/callCenter/CallCenterPhoneActiveSession';
import { CallCoachingPanel } from '@/components/callCenter/CallCoachingPanel';
import { CallSentimentIndicator } from '@/components/callCenter/CallSentimentIndicator';
import { getLastDialedNumber, setLastDialedNumber } from '@/lib/callCenterLastDialed';
import { CallbacksPendingList } from '@/components/callCenter/CallbacksPendingList';
import { PowerDialerControls } from '@/components/callCenter/PowerDialerControls';
import {
  DIAL_PAD_FLAT,
  DIAL_PAD_LETTERS,
  dialPadTapFeedback,
  normalizeDialPhoneToE164,
} from '@/lib/callCenter/dialPad';
import { copyTextToClipboard } from '@/lib/copyTextToClipboard';
import { formatSecondsAsClock } from '@/lib/formatClockDuration';
import { invokeCallCenterStartCall } from '@/lib/callCenter/invokeCallCenterStartCall';
import { clearActiveCallFromRemote, formatWebRtcErrorForUi, setActiveCrmCall } from '@/lib/telnyxWebRTCStore';

/** Tamaño máximo de tecla (se reduce si la columna es estrecha). ~46px para que no se vean “grandazas”. */
const DIAL_KEY_MAX_PX = 46;
/** Tamaño máximo del botón circular de llamar/colgar. */
const PHONE_CALL_BTN_MAX_PX = 54;

const PANEL_MAX_W = 480;
/** Tope del historial hasta medir la columna del teléfono (ResizeObserver). */
const HISTORY_FALLBACK_MAX_H = 'min(70vh, calc(100vh - 112px))';
const FAB_SIZE = 56;
const GAP = 16;

/** Solo si no hubo medición de la columna del teclado (p. ej. auto-llamada al abrir); acotado para no replicar el ~70vh del historial antes de acoplar. */
function fabDialerFallbackBodyHeightPx(): number {
  if (typeof window === 'undefined') return 440;
  return Math.ceil(Math.min(window.innerHeight * 0.55, 520, window.innerHeight - 112));
}

interface CallRow {
  id: string;
  lead_id: string | null;
  duration: number | null;
  call_status: string | null;
  created_at: string;
  destination_phone: string | null;
}

interface LeadInfo {
  id: string;
  name: string | null;
  phone: string | null;
}

function dialPhoneForRow(c: CallRow, leadMap: Record<string, LeadInfo>): string {
  const dest = c.destination_phone?.trim();
  if (dest) return dest;
  if (c.lead_id && leadMap[c.lead_id]?.phone) return leadMap[c.lead_id].phone!.trim();
  return '';
}

function webrtcStatusLabel(status: 'idle' | 'connecting' | 'connected' | 'error'): string {
  switch (status) {
    case 'idle':
      return 'Desconectado';
    case 'connecting':
      return 'Conectando…';
    case 'connected':
      return 'Listo';
    case 'error':
      return 'Error';
    default:
      return '—';
  }
}

/** Botón FAB circular del dialer — extraído para evitar quirks de esbuild con condicionales JSX. */
function DialerFabButton({
  open,
  onCall,
  hasActiveCallSession,
  webrtcVoiceReady,
  fabBottom,
  right,
  setOpen,
}: {
  open: boolean;
  onCall: boolean;
  hasActiveCallSession: boolean;
  webrtcVoiceReady: boolean;
  fabBottom: number;
  right: number;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <button
      type="button"
      className={
        hasActiveCallSession && webrtcVoiceReady && !open ? 'sf-fab-phone-attention' : undefined
      }
      aria-label={
        open
          ? onCall
            ? 'Marcador abierto — llamada en curso'
            : 'Cerrar marcador'
          : hasActiveCallSession && webrtcVoiceReady
            ? 'Llamada activa — abrir marcador'
            : 'Abrir marcador telefónico'
      }
      aria-expanded={open}
      onClick={() =>
        setOpen((o) => {
          if (!o) {
            primeDtmfAudio();
            void resumeAllContextsForSyntheticRingback(10, 35);
            return true;
          }
          if (onCall) return true;
          return false;
        })
      }
      style={{
        position: 'fixed',
        zIndex: 10051,
        bottom: fabBottom,
        right,
        width: FAB_SIZE,
        height: FAB_SIZE,
        borderRadius: '50%',
        border: 'none',
        boxSizing: 'border-box',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
        backgroundColor: 'var(--p-color-bg-fill-success)',
        color: 'var(--p-color-text-success-on-bg-fill)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        cursor: 'pointer',
        pointerEvents: 'auto',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--p-color-bg-fill-success-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--p-color-bg-fill-success)';
      }}
    >
      <span style={{ display: 'flex', width: 24, height: 24, alignItems: 'center', justifyContent: 'center' }}>
        <Icon source={PhoneIcon} tone="inherit" />
      </span>
    </button>
  );
}

function FloatingDialerPanel({
  ctx,
  tenantId,
  userId,
  hideFab = false,
}: {
  ctx: CallCenterContextValue;
  tenantId: string;
  userId: string;
  /** Oculta el botón FAB circular propio (cuando se integra en SpeedDial). */
  hideFab?: boolean;
}) {
  const toast = useAdminToast();
  const storeNavigate = useStoreNavigate();

  const [open, setOpen] = useState(false);

  // Escuchar evento custom para abrir desde SpeedDial (cross-tree communication)
  // Soporta `detail.number` opcional para pre-cargar un teléfono (p. ej. desde WhatsApp).
  useEffect(() => {
    const handler = (ev: Event) => {
      primeDtmfAudio();
      void resumeAllContextsForSyntheticRingback(10, 35);
      const detail = (ev as CustomEvent<{ number?: string }>).detail;
      const preset = detail?.number?.trim();
      if (preset) setNumber(preset);
      setOpen(true);
    };
    window.addEventListener('sf:open-dialer', handler);
    return () => window.removeEventListener('sf:open-dialer', handler);
  }, []);

  const [number, setNumber] = useState('');
  const [lastDialed, setLastDialed] = useState(() => getLastDialedNumber());
  const [calling, setCalling] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [leadMap, setLeadMap] = useState<Record<string, LeadInfo>>({});
  const startCallLockRef = useRef(false);
  const prevCallPhaseRef = useRef<string>('none');

  const {
    status: webrtcStatus,
    error: webrtcError,
    webrtcUsername,
    activeCallId: webrtcLiveCallId,
    callPhase: webrtcCallPhase,
    callDirection: webrtcCallDirection,
    remoteDialNumber,
    activeCrmCallId,
    crmCallStatus,
    hangupFromUI,
    elapsedSeconds,
    hangingUp,
    setEmbeddedCallInFloatingDialer,
    isMuted,
    isOnHold,
    toggleMute,
    toggleHold,
    transferCall,
    warmTransferCall,
    warmTransferActive,
    isRecordingPaused,
    toggleRecordingPause,
    powerDialerRunning,
    powerDialerProgress,
    stopPowerDialer,
  } = ctx;

  const useBrowserForCalls = webrtcStatus === 'connected';
  const onCall = webrtcCallPhase !== 'none';
  /**
   * Sesión de llamada en store: fase CRM/WebRTC **o** pata activa (`pending`/UUID) para no “perder” auto-llamada
   * si `callPhase` va un tick detrás del INSERT o el socket está en `connecting`.
   */
  const hasActiveCallSession =
    webrtcCallPhase !== 'none' ||
    !!(webrtcLiveCallId != null && String(webrtcLiveCallId).trim() !== '');
  const webrtcVoiceReady =
    webrtcStatus === 'connected' ||
    webrtcStatus === 'connecting' ||
    (webrtcStatus === 'error' && hasActiveCallSession);
  /** Llamada visible en FAB: única superficie (oculta tarjeta esquina del contexto). */
  const webrtcActiveCallUi = hasActiveCallSession && webrtcVoiceReady;

  useEffect(() => {
    if (!ctx) return;
    ctx.setEmbeddedCallInFloatingDialer(webrtcActiveCallUi);
    return () => {
      ctx.setEmbeddedCallInFloatingDialer(false);
    };
  }, [ctx, webrtcActiveCallUi]);

  /**
   * Abrir panel **antes del paint** si hay llamada: con FAB cerrado, `useEffect` puede llegar tarde y el agente no ve “Timbrando”.
   * Pulso en FAB (CSS) como respaldo el mismo frame.
   */
  const prevHadCallUiRef = useRef(false);
  useLayoutEffect(() => {
    if (webrtcActiveCallUi) {
      setOpen(true);
    }
    if (webrtcActiveCallUi && !prevHadCallUiRef.current) {
      primeDtmfAudio();
      void resumeAllContextsForSyntheticRingback(14, 45);
    }
    prevHadCallUiRef.current = webrtcActiveCallUi;
  }, [webrtcActiveCallUi]);

  const loadHistory = useCallback(async () => {
    if (!tenantId) return;
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_calls')
        .select('id, lead_id, duration, call_status, created_at, destination_phone')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      const rows = (data ?? []) as CallRow[];
      setCalls(rows);
      const leadIds = [...new Set(rows.map((r) => r.lead_id).filter(Boolean))] as string[];
      const map: Record<string, LeadInfo> = {};
      if (leadIds.length > 0) {
        const { data: leads } = await supabase.from('crm_leads').select('id, name, phone').in('id', leadIds);
        (leads ?? []).forEach((l: LeadInfo) => {
          map[l.id] = l;
        });
      }
      setLeadMap(map);
    } catch {
      setCalls([]);
      setLeadMap({});
    } finally {
      setHistoryLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) void loadHistory();
  }, [open, tenantId, loadHistory]);

  /** Refresca sesión al abrir el panel para que el siguiente invoke a Edge Functions no espere getSession. */
  useEffect(() => {
    if (!open) return;
    void supabase.auth.getSession();
  }, [open]);

  /** Al colgar (fase WebRTC → none), refrescar historial como en marcador completo. */
  useEffect(() => {
    const prev = prevCallPhaseRef.current;
    prevCallPhaseRef.current = webrtcCallPhase;
    if (!tenantId) return;
    if (prev !== 'none' && webrtcCallPhase === 'none') {
      void loadHistory();
    }
  }, [webrtcCallPhase, tenantId, loadHistory]);

  /** Tiempo real: estado/duración de llamadas sin recargar la página (Supabase Realtime). */
  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase
      .channel(`sf-floating-dialer-crm-calls-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_calls',
          filter: `tenant_id=eq.${tenantId}`,
        },
        () => {
          void loadHistory();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, loadHistory]);

  const append = useCallback((d: string) => {
    dialPadTapFeedback(d);
    setNumber((p) => p + d);
  }, []);
  const backspaceOne = useCallback(() => setNumber((p) => p.slice(0, -1)), []);

  /** Teclas 0–9 * # y Retroceso (fuera del campo número) con el panel abierto. */
  useDialerPhysicalKeyboard({
    enabled: open && !onCall,
    onDigit: append,
    onBackspace: backspaceOne,
  });

  const dialerColumnRef = useRef<HTMLDivElement>(null);
  const panelBodyRef = useRef<HTMLDivElement>(null);
  const [dialerColumnHeight, setDialerColumnHeight] = useState<number | null>(null);
  /**
   * Copia de la altura de la **columna del teclado** (tras acoplar, fila ≈ esa altura).
   * No usar `max(fila, cuerpo)`: el cuerpo puede ser ~70vh antes de acoplar el historial → panel “grandazo”.
   */
  const fabIdleRowHeightRef = useRef<number | null>(null);
  const fabIdleLeftWidthRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      fabIdleRowHeightRef.current = null;
      fabIdleLeftWidthRef.current = null;
      setDialerColumnHeight(null);
      return;
    }
    if (onCall) {
      return;
    }

    const el = dialerColumnRef.current;
    if (!el) return;

    const measureDialer = (): void => {
      const r = el.getBoundingClientRect();
      setDialerColumnHeight(r.height);
      const hi = Math.ceil(r.height);
      if (hi > 0) fabIdleRowHeightRef.current = hi;
      const wi = Math.round(r.width);
      if (wi > 0) fabIdleLeftWidthRef.current = wi;
    };

    measureDialer();
    const roDialer = new ResizeObserver(measureDialer);
    roDialer.observe(el);
    return () => {
      roDialer.disconnect();
    };
  }, [open, onCall, number, webrtcError, calling, historyLoading, calls.length]);

  /** Mismo número en el campo que en el store (manual o auto-llamada). */
  useEffect(() => {
    if (!onCall) return;
    const r = remoteDialNumber?.trim();
    if (r) setNumber(r);
  }, [onCall, remoteDialNumber]);

  const handleCall = useCallback(async () => {
    if (onCall || startCallLockRef.current || calling) return;
    if (!useBrowserForCalls) {
      toast.error('Teléfono no conectado', 'Espera a que el teléfono web se conecte antes de llamar.');
      return;
    }
    const raw = number.trim();
    /** Vacío: solo rellenar con el último número (no llamar); el usuario pulsa Llamar otra vez si quiere marcar. */
    if (!raw) {
      const last = getLastDialedNumber();
      if (last) {
        setNumber(last);
        toast.info('Último número cargado en el campo. Revisa o edita y pulsa Llamar para marcar.');
        return;
      }
      toast.error('Sin número', 'Escribe un número o marca antes al menos una vez para tener un último número.');
      return;
    }
    const toNumber = normalizeDialPhoneToE164(raw);
    if (toNumber.length < 10) {
      toast.error('Número inválido', 'Indica al menos 8 dígitos (ej. 987654321).');
      return;
    }
    startCallLockRef.current = true;
    /** Mismo tick que el clic: desbloquea Web Audio para el timbrado local tras start-call (async). */
    primeDtmfAudio();

    // UI optimista: mostrar "timbrando" de inmediato, sin esperar la Edge Function (~1-2s).
    // Si la API falla, revertimos al estado idle con clearActiveCallFromRemote().
    if (useBrowserForCalls) {
      syncWebRtcStoreFromOutboundCrmInsert({
        destinationPhone: toNumber,
        crmCallId: null,
        crmStatus: 'initiated',
      });
    } else {
      setCalling(true);
    }

    try {
      const out = await invokeCallCenterStartCall({
        body: { to_number: toNumber },
        useWebrtc: useBrowserForCalls,
        webrtcUsername,
      });
      // `=== false` (no `!out.ok`): con strictNullChecks off TS no discrimina la unión por truthiness.
      if (out.ok === false) {
        clearActiveCallFromRemote();
        toast.error(out.title, out.detail);
        return;
      }
      // Enlazar CRM call ID al store (para Realtime y fase UI)
      if (useBrowserForCalls && out.call_id) {
        setActiveCrmCall(out.call_id, 'initiated', { forceBusinessOutbound: true });
      }
      setLastDialedNumber(raw.trim());
      setLastDialed(raw.trim());
      toast.success('Llamada iniciada', out.message ?? 'Conectando…');
      void loadHistory();
    } catch (e: unknown) {
      clearActiveCallFromRemote();
      toast.error('Error', e instanceof Error ? e.message : 'Error al iniciar la llamada.');
    } finally {
      startCallLockRef.current = false;
      setCalling(false);
    }
  }, [
    number,
    onCall,
    calling,
    toast,
    useBrowserForCalls,
    webrtcUsername,
    tenantId,
    loadHistory,
  ]);

  /** Escape cierra el panel; Enter en el campo número llama (un solo listener; evita doble invoke). */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !onCall) {
        setOpen(false);
        return;
      }
      if (e.key !== 'Enter' || e.repeat || onCall || calling || startCallLockRef.current || !useBrowserForCalls) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-sf-dialer-number-field]')) {
        e.preventDefault();
        void handleCall();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCall, calling, handleCall]);

  const pasteFromClipboard = useCallback(async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t?.trim()) setNumber(t.trim());
    } catch {
      toast.error('No se pudo pegar', 'Permite el acceso al portapapeles o usa Ctrl+V en el campo.');
    }
  }, [toast]);

  const redialLast = useCallback(() => {
    if (!lastDialed.trim()) return;
    setNumber(lastDialed.trim());
  }, [lastDialed]);

  const copyPhoneToClipboard = useCallback(
    async (phone: string) => {
      if (!phone.trim()) return;
      const ok = await copyTextToClipboard(phone);
      if (ok) {
        toast.success('Número copiado');
      } else {
        toast.error('No se pudo copiar', 'Selecciona el número en pantalla y usa Ctrl+C.');
      }
    },
    [toast]
  );

  const openLeadDetail = useCallback(
    (leadId: string) => {
      if (!leadId) return;
      setOpen(false);
      storeNavigate(ROUTES.APPS_CRM_LEAD_DETAIL.replace(':id', leadId));
    },
    [storeNavigate]
  );

  const bottom = 24;
  const right = 24;
  const fabBottom = bottom;
  const panelBottom = fabBottom + FAB_SIZE + GAP;

  const panelWidth = Math.min(PANEL_MAX_W, typeof window !== 'undefined' ? window.innerWidth - 32 : PANEL_MAX_W);
  const webrtcUiError = formatWebRtcErrorForUi(webrtcError);
  /** Misma altura que la columna del teclado (y la fila una vez acoplada al historial). */
  const sessionPanelHeightPx = (() => {
    if (!onCall) return null;
    const h = dialerColumnHeight ?? fabIdleRowHeightRef.current ?? 0;
    return h > 0 ? h : fabDialerFallbackBodyHeightPx();
  })();

  /** Ancho columna izquierda = medido del teclado, o ~46% del panel (auto-llamada sin reposo). Evita % sobre min-content. */
  const fabSessionLeftWidthPx =
    fabIdleLeftWidthRef.current != null && fabIdleLeftWidthRef.current > 0
      ? fabIdleLeftWidthRef.current
      : Math.max(1, Math.round(panelWidth * 0.46));

  /** Portal a `body`: Polaris `Frame` usa transform y hace que `fixed` se ancle al frame (flujo + scroll). */
  const floatingUi = (
    <>
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Marcador telefónico"
          style={{
            position: 'fixed',
            zIndex: 10050,
            bottom: panelBottom,
            right,
            left: 'auto',
            top: 'auto',
            width: panelWidth,
            maxWidth: `min(${PANEL_MAX_W}px, calc(100vw - 32px))`,
            pointerEvents: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ position: 'relative', width: '100%' }}>
            <Card padding="0" roundedAbove="sm">
            <div style={{ minHeight: 0, overflow: 'hidden' }}>
              <div
                ref={panelBodyRef}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: onCall ? 'stretch' : 'flex-start',
                  boxSizing: 'border-box',
                  ...(onCall && sessionPanelHeightPx != null
                    ? {
                        height: sessionPanelHeightPx,
                        minHeight: sessionPanelHeightPx,
                        maxHeight: sessionPanelHeightPx,
                        overflow: 'hidden',
                      }
                    : { minHeight: 0, overflow: 'hidden' }),
                }}
              >
              {onCall && tenantId ? (
                <>
                <CallCenterPhoneActiveSession
                  surface="fab"
                  tenantId={tenantId}
                  crmCallId={activeCrmCallId}
                  crmCallStatus={crmCallStatus}
                  remotePhone={remoteDialNumber}
                  callPhase={webrtcCallPhase}
                  callDirection={webrtcCallDirection}
                  elapsedSeconds={elapsedSeconds}
                  onHangup={() => void hangupFromUI()}
                  onOpenDialer={() => storeNavigate(ROUTES.APPS_CRM_DIALER)}
                  hangingUp={hangingUp}
                  isMuted={isMuted}
                  isOnHold={isOnHold}
                  onToggleMute={toggleMute}
                  onToggleHold={toggleHold}
                  onTransfer={transferCall}
                  onWarmTransfer={warmTransferCall}
                  warmTransferActive={warmTransferActive}
                  isRecordingPaused={isRecordingPaused}
                  onToggleRecordingPause={toggleRecordingPause}
                  fabLeftColumnWidthPx={fabSessionLeftWidthPx}
                />
                {/* AI Call Coaching — sentimiento inline */}
                {activeCrmCallId && webrtcCallPhase === 'active' && (
                  <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--p-color-bg-surface-secondary)' }}>
                    <CallSentimentIndicator crmCallId={activeCrmCallId} variant="pill" />
                  </div>
                )}
                </>
              ) : (
              <>
              <div
                ref={dialerColumnRef}
                style={{
                  flex: FAB_DIALER_LEFT_FLEX,
                  maxWidth: FAB_DIALER_LEFT_MAX_WIDTH,
                  minWidth: 0,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  paddingBlock: 'var(--p-space-300)',
                  paddingInline: 'var(--p-space-400)',
                  background: 'var(--p-color-bg-fill-inverse)',
                }}
              >
                <BlockStack gap="200" inlineAlign="stretch">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {`Teléfono web: ${webrtcStatusLabel(webrtcStatus)}`}
                    </Text>
                    <InlineStack gap="150" align="start" blockAlign="center" wrap>
                      <Button
                        icon={ClipboardIcon}
                        variant="plain"
                        tone="inverse"
                        size="micro"
                        onClick={() => void pasteFromClipboard()}
                      >
                        Pegar
                      </Button>
                      <Button
                        variant="plain"
                        tone="inverse"
                        size="micro"
                        onClick={redialLast}
                        disabled={!lastDialed.trim()}
                      >
                        Repetir
                      </Button>
                    </InlineStack>
                    {/* Un Box con borde: fila número + retroceso (borderless evita doble borde) */}
                    <Box
                      background="bg-surface"
                      paddingBlock="0"
                      paddingInline="100"
                      borderRadius="200"
                      borderWidth="025"
                      borderColor="border"
                      width="100%"
                      minWidth="0"
                      maxWidth="100%"
                    >
                      <InlineGrid columns="minmax(0, 1fr) auto" gap="150" alignItems="center">
                        <div data-sf-dialer-number-field style={{ minWidth: 0, width: '100%' }}>
                          <TextField
                            label="Número"
                            labelHidden
                            variant="borderless"
                            value={number}
                            onChange={setNumber}
                            autoComplete="tel"
                            placeholder=""
                            type="tel"
                            size="slim"
                            onKeyDown={(e) => {
                              const d = mapKeyboardEventToDialDigit(e.nativeEvent);
                              if (d) {
                                primeDtmfAudio();
                                playDtmfKey(d);
                              }
                            }}
                          />
                        </div>
                        <Button
                          icon={BackspaceIcon}
                          variant="plain"
                          size="micro"
                          onClick={(e) => {
                            e.stopPropagation();
                            backspaceOne();
                          }}
                          accessibilityLabel="Retroceso"
                          disabled={number.length === 0}
                        />
                      </InlineGrid>
                    </Box>

                    {webrtcUiError ? <Banner tone="critical">{webrtcUiError}</Banner> : null}

                    {/* WebRTC: sin banner aquí — el permiso de micrófono lo pide el navegador al pulsar "Activar" en Marcador o al conectar; evita duplicar avisos en el FAB. */}

                    {/* Teclado: números + símbolos; letras E.161 debajo de 2–9 y + bajo 0. */}
                    <div
                      onPointerDownCapture={() => primeDtmfAudio()}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        justifyItems: 'center',
                        alignItems: 'center',
                        rowGap: 'clamp(var(--p-space-100), 1.2vw, var(--p-space-200))',
                        columnGap: 'clamp(var(--p-space-150), 1.5vw, var(--p-space-200))',
                        width: '100%',
                        minWidth: 0,
                        containerType: 'inline-size',
                      }}
                    >
                      {DIAL_PAD_FLAT.map((digit) => {
                        const letters = DIAL_PAD_LETTERS[digit] ?? '';
                        const aria =
                          digit === '*'
                            ? 'Marcar asterisco'
                            : digit === '#'
                              ? 'Marcar numeral'
                              : letters
                                ? `Marcar ${digit}, ${letters}`
                                : `Marcar ${digit}`;
                        return (
                          <div
                            key={digit}
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => append(digit)}
                              aria-label={aria}
                              style={{
                                boxSizing: 'border-box',
                                width: '100%',
                                maxWidth: DIAL_KEY_MAX_PX,
                                aspectRatio: '1 / 1',
                                height: 'auto',
                                borderRadius: '50%',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0,
                                padding: 0,
                                paddingBlock: '1px',
                                background: 'rgba(255, 255, 255, 0.14)',
                                color: 'var(--p-color-text-inverse)',
                                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                                fontFamily:
                                  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                                transition: 'background 0.12s ease',
                                touchAction: 'manipulation',
                              }}
                              onMouseDown={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background =
                                  'rgba(255, 255, 255, 0.22)';
                              }}
                              onMouseUp={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background =
                                  'rgba(255, 255, 255, 0.14)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background =
                                  'rgba(255, 255, 255, 0.14)';
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 'clamp(0.65rem, calc(0.22rem + 7cqw), 1.05rem)',
                                  fontWeight: 500,
                                  lineHeight: 1,
                                }}
                              >
                                {digit}
                              </span>
                              {letters ? (
                                <span
                                  style={{
                                    fontSize: digit === '0' ? '0.5rem' : '0.4rem',
                                    fontWeight: 600,
                                    letterSpacing: digit === '0' ? 0 : '0.03em',
                                    opacity: 0.78,
                                    lineHeight: 1,
                                    marginTop: 1,
                                  }}
                                >
                                  {letters}
                                </span>
                              ) : null}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Solo Llamar en reposo; durante la llamada el Colgar está en la columna avatar. */}
                    <InlineStack gap="300" align="center" blockAlign="center" wrap>
                      <button
                        type="button"
                        aria-label="Llamar"
                        disabled={calling || !useBrowserForCalls}
                        onClick={() => void handleCall()}
                        style={{
                          width: PHONE_CALL_BTN_MAX_PX,
                          maxWidth: '100%',
                          aspectRatio: '1 / 1',
                          height: 'auto',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: calling || !useBrowserForCalls ? 'not-allowed' : 'pointer',
                          opacity: !useBrowserForCalls ? 0.5 : 1, // el 'opacity: 1' duplicado mas abajo pisaba este condicional: el boton NUNCA se veia deshabilitado
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--p-color-bg-fill-success)',
                          color: 'var(--p-color-text-success-on-bg-fill)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          touchAction: 'manipulation',
                        }}
                      >
                        {calling ? (
                          <Spinner size="small" />
                        ) : (
                          <span style={{ color: 'var(--p-color-text-success-on-bg-fill)' }}>
                            <Icon source={PhoneIcon} tone="inherit" />
                          </span>
                        )}
                      </button>
                    </InlineStack>
                </BlockStack>
              </div>

              {/* Separador vertical */}
              <Box borderInlineStartWidth="025" borderColor="border" />

              {/* Historial (solo con teclado visible). */}
              <Box
                minWidth={0}
                minHeight="0"
                style={{
                  flex: FAB_DIALER_RIGHT_FLEX,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  ...(dialerColumnHeight != null
                    ? { height: dialerColumnHeight }
                    : { height: HISTORY_FALLBACK_MAX_H, maxHeight: HISTORY_FALLBACK_MAX_H }),
                }}
              >
                <Scrollable
                  vertical
                  horizontal={false}
                  scrollbarWidth="thin"
                  shadow
                  style={{ height: '100%', maxHeight: '100%', flex: 1, minHeight: 0 }}
                >
                  <Box paddingBlock="500" paddingInline="500">
                    <BlockStack gap="400">
                      <PowerDialerControls
                        running={powerDialerRunning}
                        progress={powerDialerProgress}
                        onStop={stopPowerDialer}
                      />

                      <CallbacksPendingList
                        tenantId={tenantId}
                        userId={userId}
                        onDialNumber={(phone) => setNumber(phone)}
                      />

                      <BlockStack gap="100">
                        <Text as="h3" variant="headingSm">
                          Historial
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Toca una fila para cargar el número. Lead y copiar a la derecha.
                        </Text>
                      </BlockStack>

                      {historyLoading ? (
                        <Box paddingBlock="800">
                          <InlineStack align="center" blockAlign="center">
                            <Spinner size="small" />
                          </InlineStack>
                        </Box>
                      ) : calls.length === 0 ? (
                        <Box paddingBlock="400" paddingInline="0">
                          <Text as="p" variant="bodySm" tone="subdued">
                            Sin llamadas recientes.
                          </Text>
                        </Box>
                      ) : (
                        <BlockStack gap="0">
                          {calls.map((c, i) => {
                            const phoneRow = dialPhoneForRow(c, leadMap);
                            return (
                              <Box key={c.id}>
                                <Box
                                  paddingBlock="300"
                                  paddingInline="0"
                                  onClick={() => {
                                    if (phoneRow) setNumber(phoneRow);
                                  }}
                                  onKeyDown={(e) => {
                                    if (!phoneRow) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setNumber(phoneRow);
                                    }
                                  }}
                                  role={phoneRow ? 'button' : undefined}
                                  tabIndex={phoneRow ? 0 : undefined}
                                  style={{ cursor: phoneRow ? 'pointer' : 'default' }}
                                >
                                  <InlineStack align="space-between" blockAlign="start" gap="300">
                                    <BlockStack gap="100">
                                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                                        {c.lead_id && leadMap[c.lead_id]
                                          ? leadMap[c.lead_id].name?.trim() || leadMap[c.lead_id].phone || 'Lead'
                                          : 'Llamada'}
                                      </Text>
                                      {phoneRow ? (
                                        <Text as="span" variant="bodySm" tone="subdued">
                                          {phoneRow}
                                        </Text>
                                      ) : null}
                                      <Text as="span" variant="bodySm" tone="subdued">
                                        {crmCallStatusLabelEs(c.call_status)}
                                      </Text>
                                    </BlockStack>
                                    <BlockStack gap="150" inlineAlign="end">
                                      <InlineStack gap="100" blockAlign="center" wrap>
                                        {c.lead_id ? (
                                          <Button
                                            size="micro"
                                            variant="plain"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openLeadDetail(c.lead_id!);
                                            }}
                                          >
                                            Lead
                                          </Button>
                                        ) : null}
                                        {phoneRow ? (
                                          <Button
                                            icon={DuplicateIcon}
                                            size="micro"
                                            variant="plain"
                                            accessibilityLabel="Copiar número"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              void copyPhoneToClipboard(phoneRow);
                                            }}
                                          />
                                        ) : null}
                                      </InlineStack>
                                      <Text as="span" variant="bodySm" fontWeight="semibold">
                                        {formatSecondsAsClock(c.duration)}
                                      </Text>
                                      <Text as="span" variant="bodySm" tone="subdued">
                                        {format(new Date(c.created_at), "d 'de' MMM HH:mm", { locale: es })}
                                      </Text>
                                    </BlockStack>
                                  </InlineStack>
                                </Box>
                                {i < calls.length - 1 ? <Divider /> : null}
                              </Box>
                            );
                          })}
                        </BlockStack>
                      )}
                    </BlockStack>
                  </Box>
                </Scrollable>
              </Box>
              </>
              )}
              </div>
            </div>
          </Card>
            <div
              style={{
                position: 'absolute',
                top: 'var(--p-space-200)',
                right: 'var(--p-space-200)',
                zIndex: 2,
              }}
            >
              <Button
                icon={XIcon}
                variant="tertiary"
                disabled={onCall}
                onClick={() => setOpen(false)}
                accessibilityLabel={onCall ? 'No puedes cerrar el marcador durante la llamada' : 'Cerrar marcador'}
              />
            </div>
          </div>
        </div>
      )}

      {/* FAB circular — oculto cuando se integra en SpeedDial (hideFab) */}
      {hideFab
        ? null
        : <DialerFabButton
            open={open}
            onCall={onCall}
            hasActiveCallSession={hasActiveCallSession}
            webrtcVoiceReady={webrtcVoiceReady}
            fabBottom={fabBottom}
            right={right}
            setOpen={setOpen}
          />
      }
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(floatingUi, document.body);
  }
  return floatingUi;
}

/** FAB + panel: solo con feature CRM y `CallCenterProvider` (ver `AppLayout`). */
export function FloatingDialerWidget({ hideFab = false }: { hideFab?: boolean } = {}) {
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const { tenantId, user, loading: authLoading } = useAuth();
  const ctx = useCallCenter();

  if (flagsLoading || authLoading) return null;
  if (!isFeatureEnabled('crm')) return null;
  if (!ctx || !tenantId || !user?.id) return null;

  return <FloatingDialerPanel ctx={ctx} tenantId={tenantId} userId={user.id} hideFab={hideFab} />;
}

/** Hook para saber si el CRM/dialer está disponible (para SpeedDial). */
export function useDialerAvailable(): boolean {
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const { tenantId, user, loading: authLoading } = useAuth();
  const ctx = useCallCenter();

  if (flagsLoading || authLoading) return false;
  if (!isFeatureEnabled('crm')) return false;
  if (!ctx || !tenantId || !user?.id) return false;
  return true;
}

/** Hook para saber si hay llamada activa (para SpeedDial pulso). */
export function useDialerActive(): boolean {
  const ctx = useCallCenter();
  if (!ctx) return false;
  return ctx.callPhase !== 'none';
}
